import {
  AbsurdIpfError,
  IpfError,
} from '@ipfabric/frontend-shared-assets/utils/IpfError';
import {
  type DeviceSecurityResponse,
  type Security,
  safeAbsurd,
} from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import { getSecurityMachineFunctionByType } from 'components/SecuritySchema/helpers/getSecurityMachineFunctionByType';
import {
  type DecidingRuleScope,
  type SecuritySchemaEntryPoint,
  isDecidingRuleEntryPoint,
  isSchemaScopeEntryPoint,
} from 'components/SecuritySchema/model/SecuritySchemaEntryPoint';
import type {
  SecuritySchemaCommandFactory,
  SecuritySchemaState,
} from 'components/SecuritySchema/model/SecuritySchemaModel';
import type { Draft } from 'immer';
import {
  type HostIdentifier,
  isCloudHostIdentifier,
  isIdHostIdentifier,
  isSnHostIdentifier,
} from 'model/HostIdentifier';
import { createUrl } from 'utils/createUrl';
import { withSecuritySchemaLoadingIndicator } from '../helpers/withSecuritySchemaLoadingIndicator';

import { resolveReferencedTests } from 'components/SecuritySchema/helpers/resolveReferencedTests';
import {
  type SecuritySchemaDataSource,
  isSecuritySchemaApiDataSource,
  isSecuritySchemaDirectDataSource,
} from 'components/SecuritySchema/model/SecuritySchemaDataSource';
import { getRuleChainInitialPage } from 'components/SecuritySchema/model/helpers/getRuleChainInitialPage';
import { nonEmptyArray } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import type { ApiRequestFunction } from 'utils/request/useApi';
import { isRuleChainScope } from '../SecuritySchemaScope';

interface LoadDeviceSecurityCommand {
  dataSource: SecuritySchemaDataSource;
  entryPoint: SecuritySchemaEntryPoint;
}

const getSecurityUrl = (
  hostIdentifier: HostIdentifier,
  cloudDirection: 'inbound' | 'outbound' | undefined,
): string => {
  if (isCloudHostIdentifier(hostIdentifier)) {
    if (cloudDirection === undefined) {
      throw new IpfError(
        'GENERIC',
        'Cloud security lookup requires a direction',
      );
    }
    return createUrl('/security/cloud', {
      constructId: hostIdentifier.cloudConstructId,
      snapshot: hostIdentifier.snapshotId,
      direction: cloudDirection,
    });
  }
  if (isIdHostIdentifier(hostIdentifier)) {
    return createUrl('/security', { id: hostIdentifier.hostId });
  }
  if (isSnHostIdentifier(hostIdentifier)) {
    return createUrl('/security', {
      snapshot: hostIdentifier.snapshotId,
      sn: hostIdentifier.hostSn,
    });
  }
  throw new AbsurdIpfError(hostIdentifier, 'unknown host identifier type');
};

/**
 * Get action of rule under given index, or default action when index === ruleChain.rules.length
 */
const getActionFromChain = (
  ruleChain: Security.RuleChain,
  index: number,
): Security.Action =>
  index === ruleChain.rules.length
    ? ruleChain.defaultAction
    : // Fixed noUncheckedIndexedAccess NIM-19408
      ruleChain.rules[index]!.action;

/**
 * Initialize state of device security to focus rule that decided a path lookup event
 */
const setupStateForDecidingRule = (
  draft: Draft<SecuritySchemaState>,
  decidingRuleScope: DecidingRuleScope,
  securityMachine: Security.SecurityMachine,
  securityType: Security.SecurityType,
) => {
  draft.breadcrumbs = [];
  draft.highlightedRules = {};

  // All but the last index should point to a rule with EXECUTE action
  const execRulesIndexes = nonEmptyArray.init(decidingRuleScope.rulePath);
  const lastRuleIndex = nonEmptyArray.last(decidingRuleScope.rulePath);

  // There may be a sequence of hidden chains at the beginning,
  // all chain from first visible chain shall be in breadcrumbs too.
  // This should be already handled in data from api, but in case
  // it is not, we ensure that we display all chains after the first
  // visible one.
  let passedHiddenPart = false;

  const lastChainName = execRulesIndexes.reduce(
    (ruleChainName: string, index: number) => {
      // Fixed noUncheckedIndexedAccess NIM-19408
      const ruleChain = securityMachine.ruleChains[ruleChainName]!;
      draft.highlightedRules[ruleChainName] = index;
      const referredAction = getActionFromChain(ruleChain, index);
      if (referredAction.type !== 'exec') {
        throw new IpfError(
          'GENERIC',
          'Malformed RulePath - only last referred action may be ActionExec',
        );
      }
      if (!ruleChain.hideInUi) {
        passedHiddenPart = true;
      }
      if (passedHiddenPart) {
        draft.breadcrumbs.push({
          ruleChainName: ruleChain.name,
          securityType,
        });
      }
      return referredAction.chainName;
    },
    securityMachine.entryChainName,
  );
  draft.highlightedRules[lastChainName] = lastRuleIndex;

  draft.scope = {
    name: lastChainName,
    securityType: decidingRuleScope.type,
    type: 'ruleChain',
  };
};

const getResponseFromDataSource = (
  dataSource: SecuritySchemaDataSource,
  entryPoint: SecuritySchemaEntryPoint,
  apiRequest: ApiRequestFunction,
): Promise<DeviceSecurityResponse> => {
  if (isSecuritySchemaApiDataSource(dataSource)) {
    // A cloud entry point carries the direction of the machine to fetch; other
    // entry points / host identifiers ignore it.
    const cloudDirection = isDecidingRuleEntryPoint(entryPoint)
      ? entryPoint.cloudDirection
      : undefined;
    return apiRequest<DeviceSecurityResponse>({
      url: getSecurityUrl(dataSource.hostIdentifier, cloudDirection),
    });
  }
  if (isSecuritySchemaDirectDataSource(dataSource)) {
    return Promise.resolve(dataSource.data);
  }
  throw new AbsurdIpfError(dataSource, 'unknown data source type');
};

export const loadDeviceSecurity: SecuritySchemaCommandFactory<
  LoadDeviceSecurityCommand
> = (cmdContext) => (command) => {
  withSecuritySchemaLoadingIndicator(cmdContext)(async () => {
    const deviceSecurityResponse = await getResponseFromDataSource(
      command.dataSource,
      command.entryPoint,
      cmdContext.apiRequest,
    );
    const deviceSecurity = pipe(
      deviceSecurityResponse.security,
      nullable.chain(resolveReferencedTests),
    );
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
    cmdContext.updateState((draft) => {
      draft.deviceSecurity = deviceSecurity;
      draft.hostname = deviceSecurityResponse.hostname;
      if (deviceSecurity === null) {
        return;
      }
      if (isSchemaScopeEntryPoint(command.entryPoint)) {
        draft.scope = command.entryPoint;
        if (
          isRuleChainScope(command.entryPoint) &&
          command.entryPoint.highlightedRuleIndex !== undefined
        ) {
          draft.highlightedRules[command.entryPoint.name] =
            command.entryPoint.highlightedRuleIndex;
        }
      } else if (isDecidingRuleEntryPoint(command.entryPoint)) {
        const securityMachine =
          getSecurityMachineFunctionByType[command.entryPoint.type](
            deviceSecurity,
          );
        if (!securityMachine) {
          return;
        }
        setupStateForDecidingRule(
          draft,
          command.entryPoint,
          securityMachine,
          command.entryPoint.type,
        );
      } else {
        safeAbsurd(command.entryPoint);
      }
      draft.page = getRuleChainInitialPage(draft);
    });
  });
};
